import type { ChatUIMessage } from './types'
import type { LoadedSession } from './api'

// Client-side mirror of SMS_BRIDGE_MARKER in server/src/lib/plan-offer.ts —
// the invisible zero-width anchor appended to every post-calc bridge. Used
// here only to place the restored calc card at its true position (right
// BEFORE the bridge that narrates it — the bridge says "the card above").
// Keep byte-identical with the server constant.
const SMS_BRIDGE_MARKER = '\u200B\u200C\u200B\u200C'

// Rebuilds the in-memory ChatUIMessage list for a restored conversation
// (resume-on-reload and the /tdee/sessions/<uuid> debug surface).
//
// Layout contract (matches what the live session rendered):
//   [ ...interview text..., calc card, bridge text (marker), CTA card,
//     ...post-result Q&A text... ]
// The server transcript only stores the TEXT messages, so the calc + CTA
// cards are re-synthesized from the stored result and spliced around the
// LAST bridge marker (recalcs emit a new bridge each time; the stored result
// is the latest calc, so it belongs to the latest bridge). Legacy sessions
// without a marker (pre-2026-06-10 narratives / intl plan offers) fall back
// to appending both cards at the end.
//
// The CTA card is omitted when the result is flagged minor (defense-in-depth;
// minors never get a stored result in the first place).
export function buildResumedMessages(data: LoadedSession): ChatUIMessage[] {
  const texts: ChatUIMessage[] = data.messages
    .filter((m) => m.content.trim())
    .map((m) => ({ role: m.role, kind: 'text' as const, content: m.content }))

  const result = data.result
  if (!result) return texts

  const calcMsg: ChatUIMessage = { role: 'assistant', kind: 'calc', payload: result }
  const ctaMsg: ChatUIMessage | null = result.is_minor
    ? null
    : {
        role: 'assistant',
        kind: 'cta',
        handoffToken: result.handoff_token ?? null,
        country: result.country ?? null,
        tdeeRecommended: result.tdee_recommended ?? null,
        tdeeLow: result.tdee_low ?? null,
        tdeeHigh: result.tdee_high ?? null,
        isMinor: result.is_minor ?? false,
        hasExistingHandoff: result.has_existing_handoff ?? false,
        isGlp1: result.on_glp1 ?? false,
        intent: result.intent,
        honestPlan: result.honest_plan ?? null,
      }

  let bridgeIdx = -1
  for (let i = texts.length - 1; i >= 0; i--) {
    const m = texts[i]
    if (m.kind === 'text' && m.role === 'assistant' && m.content.includes(SMS_BRIDGE_MARKER)) {
      bridgeIdx = i
      break
    }
  }

  if (bridgeIdx === -1) {
    return ctaMsg ? [...texts, calcMsg, ctaMsg] : [...texts, calcMsg]
  }
  const out = [...texts]
  // Insert order matters: card before the bridge, CTA after it.
  out.splice(bridgeIdx, 0, calcMsg)
  if (ctaMsg) out.splice(bridgeIdx + 2, 0, ctaMsg)
  return out
}
